import { Button } from "@/components/ui/button";
import { Sparkles, Edit3 } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { generateAICaption } from "@/utils/shareEngine";
import { trackCaptionEvent } from "@/utils/captionAnalytics";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

interface AICaptionGeneratorProps {
  guesses: number;
  won: boolean;
  streak: number;
  puzzleNumber: number;
  onCaptionGenerated: (caption: string) => void;
}

export function AICaptionGenerator({
  guesses,
  won,
  streak,
  puzzleNumber,
  onCaptionGenerated,
}: AICaptionGeneratorProps) {
  const { toast } = useToast();
  const [tone, setTone] = useState<"friendly" | "competitive">("friendly");
  const [isGenerating, setIsGenerating] = useState(false);
  const [caption, setCaption] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    const startTime = performance.now();
    
    try {
      const generatedCaption = await generateAICaption({
        guesses,
        won,
        streak,
        puzzleNumber,
        tone,
      });
      
      const latencyMs = Math.round(performance.now() - startTime);
      
      setCaption(generatedCaption);
      setIsEditing(false);
      
      // Track generation event
      trackCaptionEvent({
        type: 'generated',
        tone,
        guesses,
        won,
        streak,
        captionLength: generatedCaption.length,
        wasEdited: false,
        latencyMs,
      });
      
      toast({
        title: "Caption generated!",
        description: "You can edit it before sharing.",
      });
    } catch (error) {
      console.error("Error generating caption:", error);
      
      // Track error event
      trackCaptionEvent({
        type: 'error',
        tone,
        guesses,
        won,
        streak,
        captionLength: 0,
        wasEdited: false,
        latencyMs: Math.round(performance.now() - startTime),
      });
      
      toast({
        title: "Generation failed",
        description: error instanceof Error ? error.message : "Could not generate caption",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseCaption = () => {
    if (caption.trim()) {
      onCaptionGenerated(caption.trim());
      
      // Track use event
      trackCaptionEvent({
        type: 'used',
        tone,
        guesses,
        won,
        streak,
        captionLength: caption.length,
        wasEdited: isEditing,
        latencyMs: 0,
      });
      
      toast({
        title: "Caption added!",
        description: "Your custom caption will be included when you share.",
      });
    }
  };

  const handleClear = () => {
    setCaption("");
    setIsEditing(false);
    onCaptionGenerated("");
    
    // Track clear event
    trackCaptionEvent({
      type: 'cleared',
      tone,
      guesses,
      won,
      streak,
      captionLength: 0,
      wasEdited: false,
      latencyMs: 0,
    });
  };

  return (
    <div className="space-y-3 p-4 bg-muted/50 rounded-lg border border-dashed">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">AI Caption (Optional)</span>
        </div>
        
        <Select value={tone} onValueChange={(value) => setTone(value as "friendly" | "competitive")}>
          <SelectTrigger className="w-[130px] h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="friendly">Friendly</SelectItem>
            <SelectItem value="competitive">Competitive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!caption && (
        <Button
          onClick={handleGenerate}
          disabled={isGenerating}
          variant="outline"
          size="sm"
          className="w-full"
        >
          <Sparkles className="h-4 w-4 mr-2" />
          {isGenerating ? "Generating..." : "Generate AI Caption"}
        </Button>
      )}

      {caption && (
        <div className="space-y-2">
          {isEditing ? (
            <Textarea
              value={caption}
              onChange={(e) => {
                setCaption(e.target.value);
                // Track edit event
                trackCaptionEvent({
                  type: 'edited',
                  tone,
                  guesses,
                  won,
                  streak,
                  captionLength: e.target.value.length,
                  wasEdited: true,
                  latencyMs: 0,
                });
              }}
              className="min-h-[60px] text-sm"
              maxLength={140}
              placeholder="Edit your caption..."
            />
          ) : (
            <div className="p-3 bg-background rounded-md border text-sm">
              {caption}
            </div>
          )}
          
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{caption.length}/140 characters</span>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => setIsEditing(!isEditing)}
              variant="ghost"
              size="sm"
              className="flex-1"
            >
              <Edit3 className="h-3 w-3 mr-1" />
              {isEditing ? "Preview" : "Edit"}
            </Button>
            
            <Button
              onClick={handleUseCaption}
              variant="default"
              size="sm"
              className="flex-1"
            >
              Use Caption
            </Button>
            
            <Button
              onClick={handleClear}
              variant="ghost"
              size="sm"
              className="flex-1"
            >
              Clear
            </Button>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={isGenerating}
            variant="outline"
            size="sm"
            className="w-full"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {isGenerating ? "Regenerating..." : "Regenerate"}
          </Button>
        </div>
      )}
    </div>
  );
}
